import { useState } from 'react';
import {
  usePipelines,
  useCreatePipeline,
  useExecutePipeline,
  useDeletePipeline,
  useLaunchInvestigation,
  useAgentInvestigation,
  useGenerateInsights,
  useDailyBrief,
  useOsintAgentStats,
  useOsintScrape,
  useOsintThreatScan,
  useDarkwebSearch,
  useDarkwebLeakCheck,
  useRunCorrelation,
  type Pipeline,
  type AgentInvestigation,
} from '../api/automation';

// ── Shared UI ──

function KPI({ label, value, sub, color = 'text-cyber-cyan' }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div className="bg-surface-tertiary rounded-xl border border-border-subtle p-4 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-400 mt-1">{label}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    running: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    active: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30',
    draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    paused: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    ready: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${colors[status] ?? colors.draft}`}>
      {status}
    </span>
  );
}

function ProgressBar({ value, className = '' }: { value: number; className?: string }) {
  return (
    <div className={`w-full h-1.5 bg-surface-primary rounded-full overflow-hidden ${className}`}>
      <div
        className="h-full bg-gradient-to-r from-cyber-cyan to-helios-600 rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, value * 100)}%` }}
      />
    </div>
  );
}

const BTN = 'px-4 py-2 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2';
const BTN_SM = 'px-3 py-1.5 rounded-lg text-xs font-medium transition-all';
const CARD = 'bg-surface-secondary rounded-xl border border-border-subtle';

type Tab = 'overview' | 'pipelines' | 'agents' | 'targeting' | 'darkweb' | 'correlation';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Command Center' },
  { key: 'pipelines', label: 'Pipelines' },
  { key: 'agents', label: 'Agents IA' },
  { key: 'targeting', label: 'Ciblage Multi-Sources' },
  { key: 'darkweb', label: 'Dark Web' },
  { key: 'correlation', label: 'Correlation' },
];

// ── Predefined pipeline templates ──

const PIPELINE_TEMPLATES = [
  {
    name: 'OSINT Full Scan',
    description: 'Investigation OSINT complète: collecte → extraction d\'entités → enrichissement graph → analyse risques → alertes',
    steps: [
      { name: 'collect', step_type: 'ingest' as const, config: { source: 'osint', depth: 'deep' }, depends_on: [], retry_count: 3, timeout_seconds: 600 },
      { name: 'extract_entities', step_type: 'transform' as const, config: { ner: true, confidence_threshold: 0.7 }, depends_on: ['collect'], retry_count: 2, timeout_seconds: 300 },
      { name: 'enrich_graph', step_type: 'enrich' as const, config: { add_to_graph: true, resolve_duplicates: true }, depends_on: ['extract_entities'], retry_count: 2, timeout_seconds: 300 },
      { name: 'risk_analysis', step_type: 'analyze' as const, config: { risk_scoring: true, anomaly_detection: true }, depends_on: ['enrich_graph'], retry_count: 1, timeout_seconds: 300 },
      { name: 'alert_notify', step_type: 'notify' as const, config: { channels: ['dashboard', 'email'] }, depends_on: ['risk_analysis'], retry_count: 3, timeout_seconds: 60 },
    ],
    tags: ['osint', 'full-scan', 'automated'],
  },
  {
    name: 'Social Media Monitor',
    description: 'Surveillance continue des réseaux sociaux: collecte multi-plateforme → analyse sentiments → corrélation → alertes',
    steps: [
      { name: 'social_collect', step_type: 'ingest' as const, config: { platforms: ['twitter', 'instagram', 'linkedin', 'facebook', 'tiktok', 'reddit'] }, depends_on: [], retry_count: 3, timeout_seconds: 600 },
      { name: 'sentiment_analysis', step_type: 'analyze' as const, config: { sentiment: true, emotion: true }, depends_on: ['social_collect'], retry_count: 2, timeout_seconds: 300 },
      { name: 'profile_correlation', step_type: 'enrich' as const, config: { cross_platform: true, identity_resolution: true }, depends_on: ['sentiment_analysis'], retry_count: 2, timeout_seconds: 300 },
      { name: 'alert_dispatch', step_type: 'notify' as const, config: { channels: ['dashboard'] }, depends_on: ['profile_correlation'], retry_count: 3, timeout_seconds: 60 },
    ],
    tags: ['social', 'monitoring', 'automated'],
  },
  {
    name: 'Threat Intelligence Pipeline',
    description: 'Pipeline de renseignement sur les menaces: dark web → IOC check → corrélation → enrichissement graph → alertes critiques',
    steps: [
      { name: 'darkweb_scan', step_type: 'ingest' as const, config: { sources: ['forums', 'marketplaces', 'paste_sites', 'telegram'] }, depends_on: [], retry_count: 3, timeout_seconds: 900 },
      { name: 'ioc_check', step_type: 'transform' as const, config: { check_types: ['ip', 'domain', 'hash', 'email'] }, depends_on: ['darkweb_scan'], retry_count: 2, timeout_seconds: 300 },
      { name: 'threat_correlate', step_type: 'analyze' as const, config: { correlation: 'temporal', threat_scoring: true }, depends_on: ['ioc_check'], retry_count: 2, timeout_seconds: 300 },
      { name: 'graph_update', step_type: 'enrich' as const, config: { add_iocs: true, link_to_entities: true }, depends_on: ['threat_correlate'], retry_count: 2, timeout_seconds: 300 },
      { name: 'critical_alert', step_type: 'notify' as const, config: { channels: ['dashboard', 'email', 'webhook'], priority: 'critical' }, depends_on: ['graph_update'], retry_count: 3, timeout_seconds: 60 },
    ],
    tags: ['threat-intel', 'darkweb', 'critical'],
  },
  {
    name: 'Biometric Watchlist',
    description: 'Surveillance biométrique: détection visages → reconnaissance → corrélation OSINT → alerte identification',
    steps: [
      { name: 'face_detect', step_type: 'ingest' as const, config: { source: 'cameras', continuous: true }, depends_on: [], retry_count: 3, timeout_seconds: 600 },
      { name: 'face_recognize', step_type: 'transform' as const, config: { gallery: 'watchlist', threshold: 0.7 }, depends_on: ['face_detect'], retry_count: 2, timeout_seconds: 120 },
      { name: 'osint_crossref', step_type: 'enrich' as const, config: { reverse_image: true, social_search: true }, depends_on: ['face_recognize'], retry_count: 2, timeout_seconds: 300 },
      { name: 'identity_alert', step_type: 'notify' as const, config: { channels: ['dashboard', 'sentinel'], priority: 'high' }, depends_on: ['osint_crossref'], retry_count: 3, timeout_seconds: 60 },
    ],
    tags: ['biometric', 'watchlist', 'sentinel'],
  },
];

// ── Main Component ──

export default function AutomationPage() {
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Automation & Orchestration</h1>
        <p className="text-sm text-gray-400 mt-1">
          Centre de commande — Pipelines automatisés, agents IA autonomes, ciblage multi-sources
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-secondary rounded-xl border border-border-subtle p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === t.key
                ? 'bg-helios-700/40 text-cyber-cyan border border-cyber-cyan/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary border border-transparent'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'pipelines' && <PipelinesTab />}
      {activeTab === 'agents' && <AgentsTab />}
      {activeTab === 'targeting' && <TargetingTab />}
      {activeTab === 'darkweb' && <DarkWebTab />}
      {activeTab === 'correlation' && <CorrelationTab />}
    </div>
  );
}

// ── Overview / Command Center ──

function OverviewTab() {
  const { data: pipelines } = usePipelines({ page: 1 });
  const { data: brief } = useDailyBrief();
  const { data: osintStats } = useOsintAgentStats();

  const running = pipelines?.items.filter((p) => p.status === 'running').length ?? 0;
  const completed = pipelines?.items.filter((p) => p.status === 'completed').length ?? 0;
  const failed = pipelines?.items.filter((p) => p.status === 'failed').length ?? 0;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KPI label="Pipelines actifs" value={running} color="text-blue-400" />
        <KPI label="Pipelines terminés" value={completed} color="text-green-400" />
        <KPI label="Pipelines échoués" value={failed} color="text-red-400" />
        <KPI label="Nouvelles entités" value={brief?.new_entities ?? 0} />
        <KPI label="Nouvelles relations" value={brief?.new_relationships ?? 0} />
        <KPI label="Alertes actives" value={brief?.active_alerts ?? 0} color="text-amber-400" />
      </div>

      {/* Daily Brief */}
      {brief && (
        <div className={`${CARD} p-6`}>
          <h3 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5" />
            </svg>
            Briefing Quotidien
          </h3>
          <p className="text-sm text-gray-300 leading-relaxed">{brief.summary}</p>
          {brief.recommendations.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-gray-400 mb-2">Recommandations</h4>
              <ul className="space-y-1">
                {brief.recommendations.map((r, i) => (
                  <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                    <span className="text-cyber-cyan mt-0.5">&#8226;</span>
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {brief.top_risks.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-red-400 mb-2">Risques Prioritaires</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {brief.top_risks.map((risk, i) => (
                  <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs text-gray-300">
                    {String(risk.description ?? risk.title ?? JSON.stringify(risk))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* OSINT Agent Stats */}
      {osintStats && (
        <div className={`${CARD} p-6`}>
          <h3 className="text-sm font-semibold text-gray-200 mb-3">OSINT Agent Intelligence</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(osintStats).map(([key, val]) => (
              <div key={key} className="text-center">
                <div className="text-lg font-bold text-cyber-cyan">{String(val)}</div>
                <div className="text-[10px] text-gray-500 capitalize">{key.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Running Pipelines */}
      {pipelines && pipelines.items.filter((p) => p.status === 'running').length > 0 && (
        <div className={`${CARD} p-6`}>
          <h3 className="text-sm font-semibold text-gray-200 mb-3">Pipelines en Cours</h3>
          <div className="space-y-3">
            {pipelines.items.filter((p) => p.status === 'running').map((p) => (
              <div key={p.id} className="bg-surface-tertiary rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-200">{p.name}</span>
                  <StatusBadge status={p.status} />
                </div>
                <ProgressBar value={p.progress} />
                <div className="text-[10px] text-gray-500 mt-1">
                  {p.step_statuses.filter((s) => s.status === 'completed').length}/{p.steps.length} étapes
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Actions Rapides</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PIPELINE_TEMPLATES.map((t) => (
            <QuickLaunchCard key={t.name} template={t} />
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickLaunchCard({ template }: { template: typeof PIPELINE_TEMPLATES[number] }) {
  const createPipeline = useCreatePipeline();
  const executePipeline = useExecutePipeline();

  function handleLaunch() {
    createPipeline.mutate(template, {
      onSuccess: (pipeline) => {
        executePipeline.mutate({ id: pipeline.id });
      },
    });
  }

  const isLoading = createPipeline.isPending || executePipeline.isPending;

  return (
    <button
      onClick={handleLaunch}
      disabled={isLoading}
      className="bg-surface-tertiary hover:bg-helios-700/20 border border-border-subtle hover:border-cyber-cyan/30 rounded-xl p-4 text-left transition-all group disabled:opacity-50"
    >
      <div className="text-sm font-medium text-gray-200 group-hover:text-cyber-cyan transition-colors">{template.name}</div>
      <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{template.description}</p>
      <div className="flex gap-1 mt-2 flex-wrap">
        {template.tags.map((tag) => (
          <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-helios-700/30 text-cyber-cyan/70 rounded">{tag}</span>
        ))}
      </div>
      {isLoading && <div className="text-[10px] text-blue-400 mt-2 animate-pulse">Lancement...</div>}
    </button>
  );
}

// ── Pipelines Tab ──

function PipelinesTab() {
  const { data: pipelineList, isLoading } = usePipelines();
  const createPipeline = useCreatePipeline();
  const executePipeline = useExecutePipeline();
  const deletePipeline = useDeletePipeline();
  const [showTemplates, setShowTemplates] = useState(false);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-200">Pipelines de Données</h2>
        <button onClick={() => setShowTemplates(!showTemplates)} className={BTN}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Nouveau Pipeline
        </button>
      </div>

      {/* Templates */}
      {showTemplates && (
        <div className={`${CARD} p-6`}>
          <h3 className="text-sm font-semibold text-gray-200 mb-4">Templates Prédéfinis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PIPELINE_TEMPLATES.map((t) => (
              <div key={t.name} className="bg-surface-tertiary rounded-lg border border-border-subtle p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-200">{t.name}</span>
                  <button
                    onClick={() => createPipeline.mutate(t, { onSuccess: () => setShowTemplates(false) })}
                    disabled={createPipeline.isPending}
                    className={`${BTN_SM} bg-cyber-cyan/20 text-cyber-cyan hover:bg-cyber-cyan/30`}
                  >
                    Créer
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-3">{t.description}</p>
                <div className="flex items-center gap-1 flex-wrap">
                  {t.steps.map((s, i) => (
                    <span key={i} className="flex items-center gap-1">
                      <span className="text-[10px] px-2 py-0.5 bg-helios-700/30 border border-border-subtle rounded text-gray-300">{s.name}</span>
                      {i < t.steps.length - 1 && <span className="text-gray-600 text-xs">→</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pipeline List */}
      {isLoading ? (
        <div className="text-center text-gray-500 py-12">Chargement...</div>
      ) : !pipelineList?.items.length ? (
        <div className={`${CARD} p-12 text-center`}>
          <div className="text-gray-500 text-sm">Aucun pipeline. Créez-en un avec les templates ci-dessus.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {pipelineList.items.map((pipeline) => (
            <PipelineCard
              key={pipeline.id}
              pipeline={pipeline}
              onExecute={() => executePipeline.mutate({ id: pipeline.id })}
              onDelete={() => deletePipeline.mutate(pipeline.id)}
              isExecuting={executePipeline.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PipelineCard({ pipeline, onExecute, onDelete, isExecuting }: { pipeline: Pipeline; onExecute: () => void; onDelete: () => void; isExecuting: boolean }) {
  return (
    <div className={`${CARD} p-5`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-200">{pipeline.name}</span>
            <StatusBadge status={pipeline.status} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{pipeline.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {pipeline.status !== 'running' && (
            <button onClick={onExecute} disabled={isExecuting} className={`${BTN_SM} bg-green-500/20 text-green-400 hover:bg-green-500/30`}>
              Exécuter
            </button>
          )}
          {pipeline.status !== 'running' && (
            <button onClick={onDelete} className={`${BTN_SM} bg-red-500/20 text-red-400 hover:bg-red-500/30`}>
              Supprimer
            </button>
          )}
        </div>
      </div>

      {/* Steps visualization */}
      <div className="flex items-center gap-1 flex-wrap mb-2">
        {pipeline.steps.map((step, i) => {
          const stepStatus = pipeline.step_statuses.find((s) => s.name === step.name);
          const color = stepStatus?.status === 'completed' ? 'border-green-500/40 text-green-400' :
                        stepStatus?.status === 'running' ? 'border-blue-500/40 text-blue-400 animate-pulse' :
                        stepStatus?.error ? 'border-red-500/40 text-red-400' :
                        'border-border-subtle text-gray-400';
          return (
            <span key={i} className="flex items-center gap-1">
              <span className={`text-[10px] px-2 py-0.5 border rounded ${color}`}>{step.name}</span>
              {i < pipeline.steps.length - 1 && <span className="text-gray-600 text-xs">→</span>}
            </span>
          );
        })}
      </div>

      {pipeline.status === 'running' && <ProgressBar value={pipeline.progress} className="mt-2" />}

      {/* Meta */}
      <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-500">
        {pipeline.schedule_cron && <span>Cron: {pipeline.schedule_cron}</span>}
        <span>Créé: {new Date(pipeline.created_at).toLocaleString()}</span>
        {pipeline.tags.length > 0 && (
          <div className="flex gap-1">
            {pipeline.tags.map((t) => (
              <span key={t} className="px-1.5 py-0.5 bg-helios-700/20 text-cyber-cyan/60 rounded">{t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Agents Tab ──

function AgentsTab() {
  const [target, setTarget] = useState('');
  const [objective, setObjective] = useState('');
  const [depth, setDepth] = useState(3);
  const [activeAgentId, setActiveAgentId] = useState<string | null>(null);
  const [modules, setModules] = useState<string[]>(['osint', 'graph', 'correlation', 'faceai']);

  const launchInvestigation = useLaunchInvestigation();
  const generateInsights = useGenerateInsights();
  const { data: agentResult } = useAgentInvestigation(activeAgentId);

  function handleLaunchAgent() {
    if (!target.trim()) return;
    launchInvestigation.mutate(
      {
        target: target.trim(),
        objective: objective || undefined,
        depth,
        modules,
        auto_correlate: true,
      },
      { onSuccess: (res) => setActiveAgentId(res.id) }
    );
  }

  function handleGenerateInsights() {
    generateInsights.mutate(
      { time_window_hours: 24, categories: ['anomalies', 'trends', 'risks'] },
      { onSuccess: (res) => setActiveAgentId(res.id) }
    );
  }

  const availableModules = [
    { id: 'osint', label: 'OSINT', desc: 'Recherche open-source' },
    { id: 'graph', label: 'Graph', desc: 'Exploration réseau' },
    { id: 'correlation', label: 'Corrélation', desc: 'Analyse croisée' },
    { id: 'faceai', label: 'Face AI', desc: 'Reconnaissance faciale' },
    { id: 'darkweb', label: 'Dark Web', desc: 'Surveillance clandestine' },
    { id: 'cyber', label: 'Cyber Intel', desc: 'Menaces cyber' },
    { id: 'sentiment', label: 'Sentiment', desc: 'Analyse sentiments' },
    { id: 'social', label: 'Réseaux Sociaux', desc: 'Profils & activité' },
  ];

  return (
    <div className="space-y-6">
      {/* Launch Agent */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Lancer un Agent Autonome</h3>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Target & objective */}
          <div className="lg:col-span-2 space-y-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Cible</label>
              <input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="Nom, domaine, email, IP, username..."
                className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-cyber-cyan/50 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Objectif (optionnel)</label>
              <textarea
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                placeholder="Trouver toutes les connexions, évaluer les risques, identifier les patterns suspects..."
                rows={2}
                className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-cyber-cyan/50 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Profondeur: {depth}</label>
              <input type="range" min={1} max={10} value={depth} onChange={(e) => setDepth(Number(e.target.value))} className="w-full accent-cyber-cyan" />
            </div>
          </div>

          {/* Module Selection */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Modules Interconnectés</label>
            <div className="space-y-1.5">
              {availableModules.map((m) => (
                <label key={m.id} className="flex items-center gap-2 p-2 bg-surface-tertiary rounded-lg border border-border-subtle hover:border-cyber-cyan/20 cursor-pointer transition-all">
                  <input
                    type="checkbox"
                    checked={modules.includes(m.id)}
                    onChange={(e) => setModules(e.target.checked ? [...modules, m.id] : modules.filter((x) => x !== m.id))}
                    className="accent-cyber-cyan"
                  />
                  <div>
                    <div className="text-xs font-medium text-gray-300">{m.label}</div>
                    <div className="text-[10px] text-gray-500">{m.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button onClick={handleLaunchAgent} disabled={!target.trim() || launchInvestigation.isPending} className={BTN}>
            {launchInvestigation.isPending ? 'Lancement...' : 'Lancer Investigation'}
          </button>
          <button onClick={handleGenerateInsights} disabled={generateInsights.isPending} className={`${BTN_SM} bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 py-2`}>
            Générer Insights Globaux
          </button>
        </div>
      </div>

      {/* Agent Results */}
      {agentResult && <AgentResultCard result={agentResult} />}
    </div>
  );
}

function AgentResultCard({ result }: { result: AgentInvestigation }) {
  return (
    <div className={`${CARD} p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-200">Agent {result.agent_type}</span>
            <StatusBadge status={result.status} />
          </div>
          {result.target && <div className="text-xs text-gray-400 mt-0.5">Cible: {result.target}</div>}
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400">{result.entities_found} entités • {result.relationships_found} relations</div>
          <ProgressBar value={result.progress} className="mt-1 w-32" />
        </div>
      </div>

      {/* Actions timeline */}
      {result.actions.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-400 mb-2">Actions de l'Agent</h4>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {result.actions.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="text-cyber-cyan font-mono w-6 text-right flex-shrink-0">#{a.step}</span>
                <span className="text-gray-300">{a.action}</span>
                {a.target && <span className="text-gray-500">→ {a.target}</span>}
                {a.entities_found > 0 && <span className="text-green-400 ml-auto">+{a.entities_found} entités</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hypotheses */}
      {result.hypotheses.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-400 mb-2">Hypothèses Générées</h4>
          <div className="space-y-2">
            {result.hypotheses.map((h) => (
              <div key={h.id} className="bg-surface-tertiary rounded-lg p-3 border border-border-subtle">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-200">{h.statement}</span>
                  <span className="text-[10px] text-cyber-cyan font-mono">{(h.confidence * 100).toFixed(0)}%</span>
                </div>
                {h.category && <span className="text-[10px] text-gray-500 mt-1 block">{h.category}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      {result.insights.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-400 mb-2">Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {result.insights.map((insight) => {
              const severityColor = insight.severity === 'critical' ? 'border-red-500/30 bg-red-500/10' :
                                    insight.severity === 'high' ? 'border-orange-500/30 bg-orange-500/10' :
                                    insight.severity === 'warning' ? 'border-yellow-500/30 bg-yellow-500/10' :
                                    'border-border-subtle bg-surface-tertiary';
              return (
                <div key={insight.id} className={`rounded-lg p-3 border ${severityColor}`}>
                  <div className="text-xs font-medium text-gray-200">{insight.title}</div>
                  <div className="text-[10px] text-gray-400 mt-1">{insight.description}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-gray-500 capitalize">{insight.category}</span>
                    <span className="text-[10px] text-cyber-cyan">{(insight.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Targeting Tab — Multi-Source Intelligence ──

function TargetingTab() {
  const [targetName, setTargetName] = useState('');
  const [targetType, setTargetType] = useState<'person' | 'organization' | 'domain' | 'topic'>('person');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [threatText, setThreatText] = useState('');

  const osintScrape = useOsintScrape();
  const threatScan = useOsintThreatScan();
  const launchInvestigation = useLaunchInvestigation();

  const searchEngines = [
    { name: 'DuckDuckGo', status: 'active', type: 'search' },
    { name: 'Google (RSS)', status: 'active', type: 'news' },
    { name: 'Bing', status: 'configured', type: 'search' },
    { name: 'Yandex', status: 'configured', type: 'search' },
  ];

  const socialPlatforms = [
    { name: 'Twitter/X', status: 'active' },
    { name: 'Instagram', status: 'active' },
    { name: 'LinkedIn', status: 'active' },
    { name: 'Facebook', status: 'active' },
    { name: 'GitHub', status: 'active' },
    { name: 'Reddit', status: 'active' },
    { name: 'TikTok', status: 'active' },
    { name: 'YouTube', status: 'active' },
    { name: 'Telegram', status: 'active' },
    { name: 'Mastodon', status: 'active' },
    { name: 'Medium', status: 'active' },
    { name: 'Dev.to', status: 'active' },
    { name: 'Keybase', status: 'active' },
    { name: 'Twitch', status: 'active' },
    { name: 'GitLab', status: 'active' },
    { name: 'Stack Overflow', status: 'active' },
    { name: 'Pinterest', status: 'active' },
    { name: 'HackerNews', status: 'active' },
    { name: 'Bitbucket', status: 'active' },
  ];

  const dataSources = [
    { name: 'DNS/WHOIS', status: 'active', type: 'domain' },
    { name: 'SSL Certificates', status: 'active', type: 'domain' },
    { name: 'Shodan', status: 'optional', type: 'network' },
    { name: 'AbuseIPDB', status: 'optional', type: 'network' },
    { name: 'HIBP', status: 'optional', type: 'breach' },
    { name: 'Ethereum RPC', status: 'active', type: 'blockchain' },
    { name: 'Bitcoin RPC', status: 'active', type: 'blockchain' },
    { name: 'Dark Web Forums', status: 'active', type: 'darkweb' },
    { name: 'Paste Sites', status: 'active', type: 'darkweb' },
  ];

  function handleFullTargeting() {
    if (!targetName.trim()) return;
    launchInvestigation.mutate(
      {
        target: targetName.trim(),
        objective: `Comprehensive ${targetType} investigation across all available sources: search engines, social media, dark web, domain intel, breach data`,
        depth: 5,
        modules: ['osint', 'graph', 'correlation', 'darkweb', 'cyber', 'social', 'sentiment'],
        auto_correlate: true,
      },
      { onSuccess: (res) => alert(`Agent lancé: ${res.id}`) }
    );
  }

  return (
    <div className="space-y-6">
      {/* Full Targeting */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Ciblage Multi-Sources Automatisé</h3>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <label className="text-xs text-gray-400 mb-1 block">Cible</label>
            <input
              value={targetName}
              onChange={(e) => setTargetName(e.target.value)}
              placeholder="Nom, email, domaine, @username..."
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder:text-gray-600 focus:border-cyber-cyan/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Type</label>
            <select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as typeof targetType)}
              className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2.5 text-sm text-gray-200 focus:border-cyber-cyan/50 focus:outline-none"
            >
              <option value="person">Personne</option>
              <option value="organization">Organisation</option>
              <option value="domain">Domaine</option>
              <option value="topic">Sujet/Topic</option>
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={handleFullTargeting} disabled={!targetName.trim() || launchInvestigation.isPending} className={`w-full ${BTN}`}>
              {launchInvestigation.isPending ? 'Lancement...' : 'Lancer Ciblage Complet'}
            </button>
          </div>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Search Engines */}
        <div className={`${CARD} p-5`}>
          <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            Moteurs de Recherche
          </h4>
          <div className="space-y-2">
            {searchEngines.map((s) => (
              <div key={s.name} className="flex items-center justify-between p-2 bg-surface-tertiary rounded-lg">
                <span className="text-xs text-gray-300">{s.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Social Platforms */}
        <div className={`${CARD} p-5`}>
          <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
            Réseaux Sociaux ({socialPlatforms.length})
          </h4>
          <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto">
            {socialPlatforms.map((p) => (
              <div key={p.name} className="flex items-center gap-1.5 p-1.5 bg-surface-tertiary rounded text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                <span className="text-gray-300 truncate">{p.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Data Sources */}
        <div className={`${CARD} p-5`}>
          <h4 className="text-sm font-semibold text-gray-200 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
            </svg>
            Sources de Données
          </h4>
          <div className="space-y-2">
            {dataSources.map((s) => (
              <div key={s.name} className="flex items-center justify-between p-2 bg-surface-tertiary rounded-lg">
                <div>
                  <span className="text-xs text-gray-300">{s.name}</span>
                  <span className="text-[10px] text-gray-500 ml-2">{s.type}</span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {s.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Web Scraper & Threat Scanner */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Scraper */}
        <div className={`${CARD} p-5`}>
          <h4 className="text-sm font-semibold text-gray-200 mb-3">Web Scraper</h4>
          <input
            value={scrapeUrl}
            onChange={(e) => setScrapeUrl(e.target.value)}
            placeholder="https://example.com"
            className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-cyber-cyan/50 focus:outline-none mb-3"
          />
          <button
            onClick={() => osintScrape.mutate({ url: scrapeUrl, extract_entities: true })}
            disabled={!scrapeUrl.trim() || osintScrape.isPending}
            className={BTN}
          >
            {osintScrape.isPending ? 'Scraping...' : 'Scraper & Extraire'}
          </button>
          {osintScrape.data && (
            <div className="mt-3 bg-surface-tertiary rounded-lg p-3 max-h-48 overflow-y-auto">
              <div className="text-xs text-gray-200 font-medium mb-1">{osintScrape.data.title}</div>
              <div className="text-[10px] text-gray-400 mb-2">{osintScrape.data.content.substring(0, 300)}...</div>
              {osintScrape.data.entities.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {osintScrape.data.entities.map((e, i) => (
                    <span key={i} className="text-[9px] px-1.5 py-0.5 bg-helios-700/30 text-cyber-cyan rounded">{e.text} ({e.type})</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Threat Scanner */}
        <div className={`${CARD} p-5`}>
          <h4 className="text-sm font-semibold text-gray-200 mb-3">Scanner de Menaces</h4>
          <textarea
            value={threatText}
            onChange={(e) => setThreatText(e.target.value)}
            placeholder="Texte ou URL à analyser pour détecter des menaces..."
            rows={3}
            className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-cyber-cyan/50 focus:outline-none resize-none mb-3"
          />
          <button
            onClick={() => threatScan.mutate({ text: threatText })}
            disabled={!threatText.trim() || threatScan.isPending}
            className={BTN}
          >
            {threatScan.isPending ? 'Analyse...' : 'Analyser Menaces'}
          </button>
          {threatScan.data && (
            <div className="mt-3">
              <div className={`text-xs font-medium mb-2 ${threatScan.data.risk_level === 'high' ? 'text-red-400' : threatScan.data.risk_level === 'medium' ? 'text-yellow-400' : 'text-green-400'}`}>
                Niveau de risque: {threatScan.data.risk_level}
              </div>
              {threatScan.data.threats.length > 0 ? (
                <div className="space-y-1">
                  {threatScan.data.threats.map((t, i) => (
                    <div key={i} className="bg-red-500/10 border border-red-500/20 rounded p-2 text-xs text-gray-300">
                      <span className="font-medium text-red-400">{t.rule_name}</span>: {t.matched_text}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-500">Aucune menace détectée</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Dark Web Tab ──

function DarkWebTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [leakIdentifiers, setLeakIdentifiers] = useState('');

  const darkwebSearch = useDarkwebSearch();
  const darkwebLeakCheck = useDarkwebLeakCheck();

  return (
    <div className="space-y-6">
      {/* Dark Web Search */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          Recherche Dark Web
        </h3>
        <div className="flex gap-3">
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Mots-clés à rechercher sur le dark web..."
            className="flex-1 bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder:text-gray-600 focus:border-red-500/50 focus:outline-none"
          />
          <button
            onClick={() => darkwebSearch.mutate({ query: searchQuery, source_types: ['forums', 'marketplaces', 'paste_sites', 'telegram'], limit: 50 })}
            disabled={!searchQuery.trim() || darkwebSearch.isPending}
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-600 to-red-800 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          >
            {darkwebSearch.isPending ? 'Recherche...' : 'Rechercher'}
          </button>
        </div>

        {darkwebSearch.data && (
          <div className="mt-4 space-y-2">
            <div className="text-xs text-gray-400">{darkwebSearch.data.total} résultats trouvés</div>
            {darkwebSearch.data.results.map((r, i) => (
              <div key={i} className="bg-surface-tertiary rounded-lg border border-red-500/10 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-200">{r.title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    r.threat_level === 'critical' ? 'bg-red-500/20 text-red-400' :
                    r.threat_level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>{r.threat_level}</span>
                </div>
                <p className="text-[10px] text-gray-400 line-clamp-2">{r.content}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500">
                  <span>{r.source_type}</span>
                  <span>{r.date}</span>
                  <span>Pertinence: {(r.relevance_score * 100).toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Leak Check */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Vérification de Fuites</h3>
        <div className="flex gap-3">
          <input
            value={leakIdentifiers}
            onChange={(e) => setLeakIdentifiers(e.target.value)}
            placeholder="Emails, domaines, usernames (séparés par des virgules)..."
            className="flex-1 bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2.5 text-sm text-gray-200 placeholder:text-gray-600 focus:border-cyber-cyan/50 focus:outline-none"
          />
          <button
            onClick={() => darkwebLeakCheck.mutate({
              identifiers: leakIdentifiers.split(',').map((s) => s.trim()).filter(Boolean),
              check_types: ['email', 'domain', 'username', 'phone'],
            })}
            disabled={!leakIdentifiers.trim() || darkwebLeakCheck.isPending}
            className={BTN}
          >
            {darkwebLeakCheck.isPending ? 'Vérification...' : 'Vérifier Fuites'}
          </button>
        </div>

        {darkwebLeakCheck.data && (
          <div className="mt-4 space-y-3">
            {darkwebLeakCheck.data.results.map((result, i) => (
              <div key={i} className="bg-surface-tertiary rounded-lg p-4 border border-border-subtle">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-200">{result.identifier}</span>
                  <span className={`text-xs font-semibold ${result.total_breaches > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {result.total_breaches > 0 ? `${result.total_breaches} fuite(s)` : 'Aucune fuite'}
                  </span>
                </div>
                {result.breaches.length > 0 && (
                  <div className="space-y-1.5">
                    {result.breaches.map((b, j) => (
                      <div key={j} className="flex items-center justify-between text-xs bg-red-500/5 border border-red-500/10 rounded p-2">
                        <div>
                          <span className="text-gray-200 font-medium">{b.name}</span>
                          <span className="text-gray-500 ml-2">{b.date}</span>
                        </div>
                        <div className="flex gap-1">
                          {b.data_types.map((dt, k) => (
                            <span key={k} className="text-[9px] px-1 py-0.5 bg-red-500/20 text-red-300 rounded">{dt}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Correlation Tab ──

function CorrelationTab() {
  const [correlationMethod, setCorrelationMethod] = useState('temporal');
  const runCorrelation = useRunCorrelation();

  const methods = [
    { id: 'temporal', name: 'Temporelle', desc: 'Analyse des patterns temporels entre événements et entités' },
    { id: 'geospatial', name: 'Géospatiale', desc: 'Clustering géographique et analyse de proximité' },
    { id: 'graph_patterns', name: 'Patterns Graph', desc: 'Détection de structures: triangles, étoiles, chaînes, cycles' },
    { id: 'behavioral', name: 'Comportementale', desc: 'Matching de patterns d\'activité entre entités' },
    { id: 'network', name: 'Réseau', desc: 'Analyse de connectivité et centralité' },
    { id: 'multi_modal', name: 'Multi-Modale', desc: 'Combinaison de toutes les méthodes de corrélation' },
  ];

  const graphPatterns = [
    { name: 'Triangle', desc: 'Trois entités mutuellement connectées', icon: '△' },
    { name: 'Star', desc: 'Une entité centrale connectée à plusieurs', icon: '✦' },
    { name: 'Chain', desc: 'Séquence linéaire de connexions', icon: '⟶' },
    { name: 'Cycle', desc: 'Boucle fermée de relations', icon: '⟳' },
    { name: 'Bipartite', desc: 'Deux groupes avec connexions croisées', icon: '⇆' },
  ];

  function handleRunCorrelation() {
    runCorrelation.mutate({ method: correlationMethod });
  }

  return (
    <div className="space-y-6">
      {/* Correlation Methods */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Méthodes de Corrélation</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {methods.map((m) => (
            <button
              key={m.id}
              onClick={() => setCorrelationMethod(m.id)}
              className={`p-4 rounded-lg border text-left transition-all ${
                correlationMethod === m.id
                  ? 'bg-helios-700/30 border-cyber-cyan/40'
                  : 'bg-surface-tertiary border-border-subtle hover:border-gray-600'
              }`}
            >
              <div className={`text-sm font-medium ${correlationMethod === m.id ? 'text-cyber-cyan' : 'text-gray-300'}`}>{m.name}</div>
              <div className="text-[10px] text-gray-500 mt-1">{m.desc}</div>
            </button>
          ))}
        </div>
        <button onClick={handleRunCorrelation} disabled={runCorrelation.isPending} className={`${BTN} mt-4`}>
          {runCorrelation.isPending ? 'Analyse en cours...' : `Lancer Corrélation ${methods.find((m) => m.id === correlationMethod)?.name}`}
        </button>

        {runCorrelation.data && (
          <div className="mt-4 bg-surface-tertiary rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <StatusBadge status={runCorrelation.data.status} />
              <span className="text-xs text-gray-400">ID: {runCorrelation.data.correlation_id}</span>
            </div>
          </div>
        )}
      </div>

      {/* Graph Patterns */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Patterns de Graphe Détectables</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {graphPatterns.map((p) => (
            <div key={p.name} className="bg-surface-tertiary rounded-lg border border-border-subtle p-4 text-center">
              <div className="text-2xl mb-2">{p.icon}</div>
              <div className="text-xs font-medium text-gray-200">{p.name}</div>
              <div className="text-[10px] text-gray-500 mt-1">{p.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Module Interconnection Map */}
      <div className={`${CARD} p-6`}>
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Carte d'Interconnexion des Modules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { from: 'OSINT', to: 'Graph', flow: 'Entités découvertes → Nœuds & relations', color: 'text-blue-400' },
            { from: 'OSINT', to: 'Face AI', flow: 'Reverse image search → Détection & identification', color: 'text-purple-400' },
            { from: 'Face AI', to: 'Sentinel', flow: 'Encodings biométriques → Contrôle d\'accès', color: 'text-green-400' },
            { from: 'Dark Web', to: 'Alertes', flow: 'Mentions & fuites → Alertes critiques', color: 'text-red-400' },
            { from: 'Graph', to: 'Corrélation', flow: 'Réseau d\'entités → Analyse de patterns', color: 'text-cyan-400' },
            { from: 'Corrélation', to: 'Agents IA', flow: 'Anomalies détectées → Investigation autonome', color: 'text-amber-400' },
            { from: 'Agents IA', to: 'Rapports', flow: 'Hypothèses & insights → Rapports automatiques', color: 'text-pink-400' },
            { from: 'Cyber Intel', to: 'Graph', flow: 'IOCs & menaces → Enrichissement réseau', color: 'text-orange-400' },
          ].map((link, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-surface-tertiary rounded-lg border border-border-subtle">
              <span className={`text-xs font-semibold ${link.color} min-w-[80px]`}>{link.from}</span>
              <span className="text-gray-600">→</span>
              <span className="text-xs font-semibold text-gray-300 min-w-[80px]">{link.to}</span>
              <span className="text-[10px] text-gray-500 ml-auto">{link.flow}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
